import { PencilSimpleIcon, StorefrontIcon } from '@phosphor-icons/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetNeighbourhoodPerks } from '@/api/neighbourhood-perks'
import { FieldPhotoImage } from '@/components/layout/field-row'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DetailsList, DetailsListItem } from '@/components/ui/details-list'
import { Flex } from '@/components/ui/flex'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import { popupFactory } from '@/factories'
import { getAssetLink } from '@/helpers/get-asset-link'

import { DetailsLabelledRowsSkeleton } from '../../../../shared/details-skeleton'
import { PerkFormModal } from './perk-form-modal'
import { PERK_IMAGES_PATH } from './perks.constants'
import * as S from './perks.styled'

interface PerkViewModalProps {
  buildingId: number
  onClose: VoidFunction
  perkId: number
}

export const PerkViewModal = ({ buildingId, onClose, perkId }: PerkViewModalProps) => {
  const { t } = useTranslation(['organisation', 'neighborhood-perks', 'common'])
  const { data, isLoading } = useGetNeighbourhoodPerks(buildingId)

  const perk = data?.find((item) => item.id === perkId)
  const isRemoved = !isLoading && !!data && !perk

  // Note: closes once the perk is gone from the list
  useEffect(() => {
    if (isRemoved) onClose()
  }, [isRemoved, onClose])

  const handleEdit = () => {
    if (!perk) return
    popupFactory.open(PerkFormModal, { buildingId, perk }, { suspendCurrent: true })
  }

  return (
    <Modal
      title={t('organisation:places.perks.viewModal.title')}
      headerIcon={StorefrontIcon}
      footer={
        <>
          <Button use="secondary" onClick={onClose}>
            {t('common:actions.close')}
          </Button>
          <Button disabled={!perk} onClick={handleEdit}>
            {t('common:actions.edit')}
            <Icon icon={PencilSimpleIcon} />
          </Button>
        </>
      }
      onClose={onClose}
    >
      {isLoading || !perk ? (
        <Flex aria-hidden direction="column" gap={20}>
          <Flex gap={16}>
            {['logo', 'image'].map((photo) => (
              <Flex key={photo} direction="column" gap={8}>
                <Typography color="black800" size="text-small" weight="semibold">
                  {t(`neighborhood-perks:form.${photo as 'image' | 'logo'}.label`)}
                </Typography>
                <S.PhotoSkeleton height="10rem" width="10rem" />
              </Flex>
            ))}
          </Flex>
          <DetailsLabelledRowsSkeleton
            badgeRow={4}
            labels={[
              t('neighborhood-perks:form.name.label'),
              t('neighborhood-perks:form.category.label'),
              t('neighborhood-perks:form.perkType.label'),
              t('organisation:places.perks.types.promoCode'),
              t('neighborhood-perks:form.tag.label'),
              t('neighborhood-perks:form.description.label'),
            ]}
          />
        </Flex>
      ) : (
        <Flex direction="column" gap={20}>
          <Flex gap={16}>
            <Flex direction="column" gap={8}>
              <Typography color="black800" size="text-small" weight="semibold">
                {t('neighborhood-perks:form.logo.label')}
              </Typography>
              <FieldPhotoImage
                alt={t('neighborhood-perks:form.logo.label')}
                src={getAssetLink({ file: perk.logo ?? undefined, path: PERK_IMAGES_PATH })}
              />
            </Flex>
            <Flex direction="column" gap={8}>
              <Typography color="black800" size="text-small" weight="semibold">
                {t('neighborhood-perks:form.image.label')}
              </Typography>
              <FieldPhotoImage
                alt={t('neighborhood-perks:form.image.label')}
                src={getAssetLink({ file: perk.image ?? undefined, path: PERK_IMAGES_PATH })}
              />
            </Flex>
          </Flex>

          <DetailsList>
            <DetailsListItem label={t('neighborhood-perks:form.name.label')}>
              {perk.name}
            </DetailsListItem>
            <DetailsListItem label={t('neighborhood-perks:form.category.label')}>
              {perk.category}
            </DetailsListItem>
            <DetailsListItem label={t('neighborhood-perks:form.perkType.label')}>
              {perk.url
                ? t('organisation:places.perks.types.website')
                : t('organisation:places.perks.types.promoCode')}
            </DetailsListItem>
            <DetailsListItem
              label={
                perk.url
                  ? t('organisation:places.perks.types.website')
                  : t('organisation:places.perks.types.promoCode')
              }
            >
              {perk.url ? (
                <S.OfferLink href={perk.url} rel="noopener noreferrer" target="_blank">
                  {perk.url}
                </S.OfferLink>
              ) : (
                (perk.link ?? EMPTY_VALUE)
              )}
            </DetailsListItem>
            <DetailsListItem label={t('neighborhood-perks:form.tag.label')}>
              <Badge color="blue" size="small" variant="light">
                {perk.tag}
              </Badge>
            </DetailsListItem>
            <DetailsListItem label={t('neighborhood-perks:form.description.label')}>
              {perk.description || EMPTY_VALUE}
            </DetailsListItem>
          </DetailsList>
        </Flex>
      )}
    </Modal>
  )
}
